import { useCallback } from 'react';

import { useNotification } from '../../../contexts/NotificationContext';
import {
  useUpdateChatSettingsDataMutationMutation,
  ChatUpdateInput,
} from '../../../generated';

interface UseUpdateChatSettingsConfig {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

interface UpdateChatSettingsParams {
  chatId: string;
  chat: ChatUpdateInput;
}

export const useUpdateChatSettings = (
  config: UseUpdateChatSettingsConfig = {}
) => {
  const { onSuccess, onError } = config;
  const { addNotification } = useNotification();

  const [updateChatSettings, { loading: isUpdating }] =
    useUpdateChatSettingsDataMutationMutation({
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
      // Reconcile the authoritative chat state after a successful write so the
      // Settings form (and its dirty baseline) reflects server truth.
      refetchQueries: ['GetChatData'],
    });

  const updateSettings = useCallback(
    async ({ chatId, chat }: UpdateChatSettingsParams) => {
      try {
        const result = await updateChatSettings({
          variables: {
            id: chatId,
            chat,
          },
        });

        // `errorPolicy: 'all'` returns GraphQL errors on the result instead of
        // rejecting; surface them (and a missing payload) as a failure so the
        // caller can keep the field dirty and retryable.
        const graphQLErrors = result.errors;
        if (graphQLErrors && graphQLErrors.length > 0) {
          throw new Error(
            graphQLErrors[0]?.message || 'Failed to update chat settings'
          );
        }
        if (!result.data?.chatUpdate) {
          throw new Error('Chat update returned no result.');
        }

        addNotification({
          title: 'Success',
          message: 'Chat settings updated successfully.',
          type: 'success',
        });
        onSuccess?.();
      } catch (updateError: unknown) {
        const error =
          updateError instanceof Error
            ? updateError
            : new Error('Failed to update chat settings');
        onError?.(error);
        addNotification({
          title: 'Error',
          message: error.message,
          type: 'error',
        });
        throw error;
      }
    },
    [updateChatSettings, addNotification, onSuccess, onError]
  );

  return {
    updateSettings,
    isUpdating,
  };
};
